import { Controller } from "react-hook-form";

import { OperationModeOptionFragment } from "@/apollo/types";
import { RadioButtonCard } from "@/components/elements/ButtonCard";
import Tooltip from "@/components/elements/Tooltip";
import { FieldErrorMessage } from "@/components/primitives/FieldErrorMessage";
import FieldLabel from "@/components/primitives/InputLabel";
import cn from "@/helpers/classNames";
import { RadioGroup } from "@headlessui/react";

import { FormValues } from "../../form";
import { getOperationModeTooltip } from "../helpers";

export function SyncModeSection(props: {
  operations: OperationModeOptionFragment[];
  onChange: (
    value: OperationModeOptionFragment | null,
    prevValue: OperationModeOptionFragment | null,
  ) => void;
}) {
  if (props.operations.length === 0) {
    return null;
  }
  return (
    <>
      <div className="mb-4">
        <FieldLabel className="leading-normal">Sync Mode</FieldLabel>
        <p className="text-xs text-muted-foreground">
          Choose how you want to sync data from your source to the destination
          object.
        </p>
      </div>
      <Controller<FormValues, "operation">
        name="operation"
        rules={{
          required: true,
          validate: (value) => {
            if (value == null) {
              return "Operation mode is required";
            }
            const isValid = props.operations.some(
              (operation) => operation.operationMode === value.operationMode,
            );
            if (!isValid) {
              return `"${value.operationMode}" is not a valid operation mode`;
            }
            return true;
          },
        }}
        render={({ field }) => {
          return (
            <>
              <RadioGroup<"div", OperationModeOptionFragment | null>
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  props.onChange(value, field.value);
                }}
                className="flex w-full items-center gap-2"
                by={(a, b) => a?.operationMode === b?.operationMode}
              >
                {props.operations.map((operation) => (
                  <Tooltip
                    key={operation.operationMode}
                    content={getOperationModeTooltip(operation.operationMode)}
                  >
                    <RadioGroup.Option value={operation}>
                      {({ checked }) => (
                        <RadioButtonCard
                          isChecked={checked}
                          className={cn(
                            "flex h-9 items-center rounded-sm px-4 text-sm capitalize hover:bg-blue-100 dark:hover:bg-blue-400/20",
                            {
                              "bg-blue-100 font-medium dark:bg-blue-400/20":
                                checked,
                            },
                          )}
                        >
                          {operation.operationMode}
                        </RadioButtonCard>
                      )}
                    </RadioGroup.Option>
                  </Tooltip>
                ))}
              </RadioGroup>
            </>
          );
        }}
      />
      <FieldErrorMessage<FormValues> name="operation" />
    </>
  );
}
